<template>
    <v-dialog
        class="agent-chat"
        maxWidth="1500px"
        height="720"
        :modelValue="props.modelValue"
        @update:modelValue="emit('update:modelValue', $event)"
    >
        <v-card>
            <v-card-title>
                <div
                    class="deepi-row align-center gap-8"
                >
                    <v-icon
                        icon="icon-stars"
                        color="#3FB8D7"
                    />
                    <p
                        class="text-body-xl text-bold text-ellipsis"
                    >
                        {{ tr('agent-chat.title') }}
                    </p>
                </div>
            </v-card-title>
            <v-card-text
                v-if="ws"
                class="container"
            >
                <div
                    ref="messagesContainer"
                    class="messages scroll-container"
                >
                    <div
                        class="message agent"
                    >
                        <p
                            class="text-body-m"
                        >
                            {{ welcomeMessage.content }}
                        </p>
                    </div>
                    <div
                        v-for="(message, index) in history"
                        class="message"
                        :class="message.role"
                        :key="index"
                    >
                        <p
                            class="text-body-m"
                        >
                            {{ message.content }}
                        </p>
                    </div>
                    <div
                        v-if="thinking"
                        class="message agent"
                    >
                        <div
                            class="loader"
                        />
                    </div>
                    <div
                        v-if="[2, 3].includes(ws.readyState)"
                        class="message error"
                    >
                        <p
                            class="text-body-m"
                        >
                            {{ tr('agent-chat.reconnecting') }}
                        </p>
                    </div>
                </div>
                <div
                    class="deepi-row align-center gap-8"
                >
                    <DeepiTextArea
                        placeholder="Enter a message (Press Shift + Enter for a new line)..."
                        :validateKeyDown="onKeyDown"
                        v-model="message"
                    />
                    <DeepiButtonIcon
                        icon="icon-log"
                        :disabled="[0, 2, 3].includes(ws.readyState) && !message"
                        @click="sendMessage"
                    />
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import dayjs, { type Dayjs } from 'dayjs';
import { storeToRefs } from 'pinia';

import { useOrganizationStore, useUserStore } from '@deepidoo/playdoo/stores';

import { useTranslations } from '../../composables';
import { useHistoryStore } from '../../stores';

export interface AgentChatProps {
    modelValue: boolean;
};

const props = defineProps<AgentChatProps>();

const emit = defineEmits<{
    'update:modelValue': [modelValue: boolean];
}>();

const queryClient = useQueryClient();
const organizationStore = useOrganizationStore();
const { currentOrganization } = storeToRefs(organizationStore);
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);
const { tr } = useTranslations();
const historyStore = useHistoryStore();
const { history } = storeToRefs(historyStore);

const messagesContainer = ref<HTMLElement | null>(null);
const ws = ref<WebSocket | null>(null);
const message = ref('');
const thinking = ref(false);
const closing = ref(false);

const welcomeMessage = computed((): { role: 'agent', content: string, timestamp: Dayjs } => {
    return {
        role: 'agent',
        content: tr('agent-chat.welcome'),
        timestamp: dayjs()
    };
});

onMounted(() => {
    connectToAgent();
});

onUnmounted(() => {
    if (!ws.value) {
        return;
    }

    closing.value = true;
    ws.value.close();
});

const connectToAgent = (): void => {
    ws.value = new WebSocket(`${import.meta.env.VITE_API_BASE_URL}agent`);

    ws.value.onopen = (): void => {
        if (!ws.value) {
            return;
        }
        ws.value.send(JSON.stringify({
            role: 'initialization',
            content: {
                userId: currentUser.value?.id,
                organizationId: currentOrganization.value?.id,
                history: history.value.map(message => ({
                    role: message.role,
                    content: message.content
                }))
            }
        }));
    };

    ws.value.onmessage = (event): void => {
        const parsed = JSON.parse(event.data);
        switch (parsed.role) {
            case 'tool':
                handleToolCall(parsed.tool, parsed.args ?? {});
                return;
            case 'agent':
                history.value.push({
                    role: 'agent',
                    content: parsed.content,
                    timestamp: dayjs()
                });
                thinking.value = false;
                return;
        }
    };

    ws.value.onerror = (): void => {
        thinking.value = false;
        ws.value?.close();
    };

    ws.value.onclose = (): void => {
        if (!closing.value) {
            setTimeout(() => {
                connectToAgent();
            }, 1000);
        }
    };
};

const getNumberArg = (args: Record<string, unknown>, key: string): number | undefined => {
    const value = args[key];
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value === 'string') {
        const parsed = Number.parseInt(value, 10);
        return Number.isNaN(parsed) ? undefined : parsed;
    }
    return;
};

const handleToolCall = (tool: string, args: Record<string, unknown>): void => {
    switch (tool) {
        case 'create_playlist':
        case 'update_playlist':
        case 'delete_playlist':
        case 'duplicate_playlist':
        case 'stuff_playlist': {
            queryClient.invalidateQueries({ queryKey: ['infinite', 'playlists'] });
            queryClient.invalidateQueries({ queryKey: ['ids', 'playlists'] });

            const playlistId = getNumberArg(args, 'playlist_id') ?? getNumberArg(args, 'id');
            if (playlistId != null) {
                queryClient.invalidateQueries({ queryKey: ['single', 'playlist', playlistId] });
                queryClient.invalidateQueries({ queryKey: ['infinite', 'playlistsContents', playlistId] });
                queryClient.invalidateQueries({ queryKey: ['ids', 'playlistsContents', playlistId] });
            }
            break;
        }
        case 'create_planning':
        case 'update_planning':
        case 'delete_planning':
        case 'duplicate_planning': {
            queryClient.invalidateQueries({ queryKey: ['infinite', 'plannings'] });
            queryClient.invalidateQueries({ queryKey: ['ids', 'plannings'] });

            const planningId = getNumberArg(args, 'planning_id') ?? getNumberArg(args, 'id');
            if (planningId != null) {
                queryClient.invalidateQueries({ queryKey: ['single', 'planning', planningId] });
                queryClient.invalidateQueries({ queryKey: ['infinite', 'planningSlots', planningId] });
            }
            break;
        }
        case 'replace_planning_slots': {
            const planningId = getNumberArg(args, 'planning_id') ?? getNumberArg(args, 'id');
            if (planningId != null) {
                queryClient.invalidateQueries({ queryKey: ['single', 'planning', planningId] });
                queryClient.invalidateQueries({ queryKey: ['infinite', 'planningSlots', planningId] });
            }
            break;
        }
        case 'broadcast_planning': {
            queryClient.invalidateQueries({ queryKey: ['infinite', 'spots'] });
            queryClient.invalidateQueries({ queryKey: ['ids', 'spots'] });
            break;
        }
        case 'create_tag':
        case 'update_tag':
        case 'delete_tag': {
            queryClient.invalidateQueries({ queryKey: ['infinite', 'tags'] });
            queryClient.invalidateQueries({ queryKey: ['ids', 'tags'] });

            const tagId = getNumberArg(args, 'tag_id') ?? getNumberArg(args, 'id');
            if (tagId != null) {
                queryClient.invalidateQueries({ queryKey: ['single', 'tag', tagId] });
            }
            break;
        }
    }
};

const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
    }
};

const sendMessage = (): void => {
    if (!ws.value || !message.value.trim().length) {
        return;
    }

    ws.value.send(JSON.stringify({
        role: 'user',
        content: message.value
    }));
    history.value.push({
        role: 'user',
        content: message.value,
        timestamp: dayjs()
    });

    thinking.value = true;
    message.value = '';
};

watch([currentOrganization, currentUser], (): void => {
    if (!ws.value || !currentOrganization.value || !currentUser.value) {
        return;
    }

    ws.value.send(JSON.stringify({
        role: 'initialization',
        content: JSON.stringify({
            userId: currentUser.value.id,
            organizationId: currentOrganization.value.id,
            history
        })
    }));
}, { deep: true });

watch([history, thinking], async (): Promise<void> => {
    await nextTick();
    if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
}, { immediate: true, deep: true });
</script>

<style scoped lang="scss">
@use 'AgentChat.scss';
</style>